import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  BackButton,
  Badge,
  Button,
  Card,
  CardDescription,
  CardTitle,
  Screen,
  Text,
  durations,
  easings,
  type WellbeingCategory,
} from "@andrum/ui";
import { MoodSelector } from "@/features/check-in/components/MoodSelector";
import { ScaleSlider } from "@/features/check-in/components/ScaleSlider";
import { useCheckInStore } from "@/stores/check-in-store";
import { useExperimentStore } from "@/stores/experiment-store";
import { useAuthStore } from "@/stores/auth-store";
import { hapticService } from "@/lib/capacitor";
import { createCheckIn } from "@/features/check-in/api/check-in-api";
import { getActiveEnrollment } from "@/features/programs/api/programs-api";
import { listRecentCheckIns } from "@/features/check-in/api/check-in-api";
import type { Mood } from "@andrum/schemas";
import type { Enrollment } from "@/features/programs/api/programs-api";
import { DailyInsightCard } from "@/features/learn/components/DailyInsightCard";
import { getReasonLabel } from "@/features/experiments/lib/recommend";
import { NpsSheet } from "@/features/learn/components/NpsSheet";
import { ClinicianHomeworkCard } from "@/features/practice/components/ClinicianHomeworkCard";
import {
  markNpsPrompted,
  shouldShowNps,
} from "@/lib/engagement/nps";
import { maybeShowLocalInsightReminder } from "@/lib/preferences/notification-prefs";
import {
  comebackCopy,
  daysSinceLastSeen,
  dismissComebackToday,
  dismissFirstRunGuide,
  firstRunProgress,
  isFirstRunGuideVisible,
  markFirstCheckInDone,
  markFirstExperimentStarted,
  shouldShowComeback,
  touchLastSeen,
} from "@/lib/engagement/session";

function isWellbeingCategory(value: string): value is WellbeingCategory {
  return (
    value === "connection" ||
    value === "movement" ||
    value === "recovery" ||
    value === "gratitude" ||
    value === "meaning" ||
    value === "presence" ||
    value === "other"
  );
}

type Panel = "home" | "checkin" | "guide";

const panelMotion = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
};

export function TodayScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const { draft, setMood, setEnergy, setStress, isComplete, reset } =
    useCheckInStore();
  const {
    getTodayAssignment,
    getExperimentForAssignment,
    hydrate,
    hydrated,
    loading,
    swapToday,
    experiments,
    assignFromCatalog,
  } = useExperimentStore();

  const [submitted, setSubmitted] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [swapping, setSwapping] = useState(false);
  const [insightBusy, setInsightBusy] = useState(false);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [weekPulse, setWeekPulse] = useState<{
    checkIns: number;
    days: number;
  } | null>(null);
  const [showFirstRun, setShowFirstRun] = useState(false);
  const [comeback, setComeback] = useState<{
    title: string;
    body: string;
    days: number;
  } | null>(null);
  const [firstProgress, setFirstProgress] = useState(firstRunProgress);
  const [panel, setPanel] = useState<Panel>("home");
  const [npsOpen, setNpsOpen] = useState(false);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 11) return t("today.greetingMorning");
    if (hour < 17) return t("today.greetingAfternoon");
    return t("today.greetingEvening");
  };

  useEffect(() => {
    const gap = daysSinceLastSeen();
    if (shouldShowComeback(3)) {
      setComeback({ ...comebackCopy(gap), days: gap });
    } else {
      setComeback(null);
    }
    touchLastSeen();
    setShowFirstRun(isFirstRunGuideVisible());
    setFirstProgress(firstRunProgress());

    if (!hydrated) void hydrate();
    void getActiveEnrollment().then(setEnrollment);
    void (async () => {
      const recent = await listRecentCheckIns(14);
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      start.setDate(start.getDate() - 6);
      const startKey = start.toISOString().slice(0, 10);
      const week = recent.filter(
        (c) => c.client_created_at.slice(0, 10) >= startKey
      );
      const days = new Set(week.map((c) => c.client_created_at.slice(0, 10)));
      setWeekPulse({ checkIns: week.length, days: days.size });

      void maybeShowLocalInsightReminder();
    })();
  }, [hydrate, hydrated]);

  const assignment = getTodayAssignment();
  const experiment = assignment
    ? getExperimentForAssignment(assignment.id)
    : null;
  const isOpen =
    assignment &&
    (assignment.status === "assigned" || assignment.status === "started");

  const handleMoodSelect = (mood: Mood) => {
    setMood(mood);
    setShowDetails(true);
  };

  const handleSubmit = () => {
    if (!isComplete() || draft.mood === null) return;
    void hapticService.success();

    markFirstCheckInDone();
    setFirstProgress(firstRunProgress());
    if (!isFirstRunGuideVisible()) setShowFirstRun(false);
    setSyncNote(null);
    setSubmitted(true);
    setWeekPulse((prev) =>
      prev
        ? { checkIns: prev.checkIns + 1, days: Math.max(prev.days, 1) }
        : { checkIns: 1, days: 1 }
    );

    const idempotencyKey = crypto.randomUUID();
    const userId = useAuthStore.getState().user?.id;
    const shouldRefreshAssignment =
      isOpen && assignment?.status === "assigned";

    void (async () => {
      const result = await createCheckIn({
        mood: draft.mood!,
        energy: draft.energy,
        stress: draft.stress,
        clientCreatedAt: new Date().toISOString(),
        idempotencyKey,
        ...(userId ? { userId } : {}),
      });
      if (!result.ok) {
        setSyncNote(result.message);
        return;
      }
      if (shouldRefreshAssignment) {
        void swapToday();
      }

      void (async () => {
        try {
          const recent = await listRecentCheckIns(30);
          const days = new Set(
            recent.map((c) => c.client_created_at.slice(0, 10))
          );
          const progress = firstRunProgress();
          const auth =
            useAuthStore.getState().status === "authenticated";
          const show = await shouldShowNps({
            authenticated: auth,
            checkInDays: days.size,
            hasUsedExperiment: progress.experiment,
          });
          if (!show) return;
          await markNpsPrompted();
          window.setTimeout(() => setNpsOpen(true), 2500);
        } catch {
          // ignore
        }
      })();
    })();
  };

  const handleReset = () => {
    reset();
    setSubmitted(false);
    setShowDetails(false);
  };

  const handleStartExperiment = () => {
    if (!assignment) return;
    void hapticService.selection();
    markFirstExperimentStarted();
    setFirstProgress(firstRunProgress());
    if (!isFirstRunGuideVisible()) setShowFirstRun(false);
    void navigate({
      to: "/experiment/$assignmentId",
      params: { assignmentId: assignment.id },
    });
  };

  const handleInsightExperiment = async (slug: string | null) => {
    void hapticService.selection();
    if (!slug) {
      void navigate({ to: "/app/explore" });
      return;
    }
    const match = experiments.find((e) => e.slug === slug);
    if (!match) {
      void navigate({ to: "/app/explore" });
      return;
    }
    if (assignment && isOpen && assignment.experimentId === match.id) {
      handleStartExperiment();
      return;
    }
    setInsightBusy(true);
    const next = await assignFromCatalog(match.id);
    setInsightBusy(false);
    if (!next) {
      void navigate({ to: "/app/explore" });
      return;
    }
    markFirstExperimentStarted();
    setFirstProgress(firstRunProgress());
    void navigate({
      to: "/experiment/$assignmentId",
      params: { assignmentId: next.id },
    });
  };

  const greetingSub = useMemo(() => {
    if (comeback) return comeback.body;
    if (showFirstRun) return t("today.subtitleFirstRun");
    return t("today.subtitleDefault");
  }, [comeback, showFirstRun, t]);

  const handleSwap = async () => {
    setSwapping(true);
    void hapticService.selection();
    await swapToday();
    setSwapping(false);
  };

  const goHome = () => {
    void hapticService.selection();
    setPanel("home");
  };

  const transition = reduce
    ? { duration: 0 }
    : { duration: durations.normal, ease: easings.enter };

  if (loading && !hydrated) {
    return (
      <Screen loading skeleton loadingLabel={t("today.loadingExperiment")} />
    );
  }

  return (
    <Screen>
      <AnimatePresence mode="wait" initial={false}>
        {panel === "guide" && showFirstRun ? (
          <motion.div
            key="guide"
            className="flex min-h-full flex-col gap-4"
            {...(reduce
              ? {}
              : {
                  initial: panelMotion.initial,
                  animate: panelMotion.animate,
                  exit: panelMotion.exit,
                  transition,
                })}
          >
            <BackButton onClick={goHome}>{t("back")}</BackButton>
            <header className="space-y-1.5">
              <Text variant="label">{t("today.firstRunLabel")}</Text>
              <Text variant="title" as="h1">
                {t("today.firstRunTitle")}
              </Text>
              <Text variant="muted" className="text-sm leading-relaxed">
                {t("today.firstRunBody")}
              </Text>
            </header>
            <ol className="space-y-2.5 text-sm leading-snug text-foreground-muted">
              <li
                className={
                  firstProgress.checkIn ? "text-secondary" : undefined
                }
              >
                {firstProgress.checkIn ? "✓" : "1."} {t("today.firstRunStep1")}
              </li>
              <li
                className={
                  firstProgress.experiment ? "text-secondary" : undefined
                }
              >
                {firstProgress.experiment ? "✓" : "2."}{" "}
                {t("today.firstRunStep2")}
              </li>
              <li>3. {t("today.firstRunStep3")}</li>
            </ol>
            <div className="mt-auto space-y-2 pt-4">
              {!firstProgress.checkIn ? (
                <Button fullWidth onClick={() => setPanel("checkin")}>
                  {t("today.firstRunCtaCheckIn")}
                </Button>
              ) : null}
              {firstProgress.checkIn && isOpen ? (
                <Button
                  fullWidth
                  variant="secondary"
                  onClick={handleStartExperiment}
                >
                  {t("today.firstRunCtaExperiment")}
                </Button>
              ) : null}
              <Button
                fullWidth
                variant="ghost"
                onClick={() => {
                  dismissFirstRunGuide();
                  setShowFirstRun(false);
                  setPanel("home");
                }}
              >
                {t("today.firstRunDismiss")}
              </Button>
            </div>
          </motion.div>
        ) : panel === "checkin" ? (
          <motion.div
            key="checkin"
            className="flex min-h-full flex-col gap-4"
            {...(reduce
              ? {}
              : {
                  initial: panelMotion.initial,
                  animate: panelMotion.animate,
                  exit: panelMotion.exit,
                  transition,
                })}
          >
            <BackButton onClick={goHome}>{t("back")}</BackButton>
            <header className="space-y-1.5">
              <Text variant="title" as="h1">
                {t("today.checkInTitle")}
              </Text>
              <Text variant="muted" className="text-sm leading-relaxed">
                {t("today.checkInLead")}
              </Text>
            </header>

            <Card className="space-y-1 border-border/80">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="done"
                    initial={reduce ? false : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: reduce ? 0 : -4 }}
                    transition={{ duration: durations.fast }}
                    className="flex flex-col items-center gap-3 py-8 text-center"
                  >
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-soft text-xl text-primary"
                      aria-hidden
                    >
                      ✓
                    </div>
                    <Text variant="heading" as="p">
                      {t("today.checkInSaved")}
                    </Text>
                    <Text variant="subtle">{t("today.checkInSavedBody")}</Text>
                    {syncNote ? (
                      <Text variant="caption" className="text-warning">
                        {syncNote}
                      </Text>
                    ) : null}
                    <div className="flex w-full flex-col gap-2 pt-2">
                      <Button fullWidth onClick={goHome}>
                        {t("done")}
                      </Button>
                      <Button
                        fullWidth
                        variant="ghost"
                        onClick={() => {
                          handleReset();
                        }}
                      >
                        {t("today.newCheckIn")}
                      </Button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={reduce ? false : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: reduce ? 0 : -4 }}
                    transition={{ duration: durations.fast }}
                    className="flex flex-col gap-5 py-2"
                  >
                    <MoodSelector
                      value={draft.mood}
                      onChange={handleMoodSelect}
                    />
                    <AnimatePresence initial={false}>
                      {showDetails ? (
                        <motion.div
                          key="details"
                          initial={
                            reduce ? false : { opacity: 0, height: 0 }
                          }
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: reduce ? "auto" : 0 }}
                          transition={{ duration: durations.normal }}
                          className="space-y-3 overflow-hidden"
                        >
                          <ScaleSlider
                            compact
                            label={t("today.energy")}
                            value={draft.energy}
                            onChange={setEnergy}
                            minLabel={t("today.energyMin")}
                            maxLabel={t("today.energyMax")}
                          />
                          <ScaleSlider
                            compact
                            label={t("today.stress")}
                            value={draft.stress}
                            onChange={setStress}
                            minLabel={t("today.stressMin")}
                            maxLabel={t("today.stressMax")}
                          />
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                    <Button
                      fullWidth
                      disabled={!isComplete()}
                      onClick={handleSubmit}
                    >
                      {t("today.saveCheckIn")}
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="home"
            className="flex flex-col gap-4 pb-2"
            {...(reduce
              ? {}
              : {
                  initial: panelMotion.initial,
                  animate: panelMotion.animate,
                  exit: panelMotion.exit,
                  transition,
                })}
          >
            <header className="space-y-1.5">
              <div className="flex items-start justify-between gap-3">
                <Text variant="title" as="h1" className="min-w-0 flex-1">
                  {comeback ? comeback.title : getGreeting()}
                </Text>
                {weekPulse && weekPulse.checkIns > 0 ? (
                  <button
                    type="button"
                    title={t("today.weekStats", {
                      count: weekPulse.checkIns,
                      days: weekPulse.days,
                    })}
                    className="shrink-0 rounded-full border border-border/50 bg-surface-muted/70 px-2.5 py-1 text-[11px] font-medium tabular-nums text-foreground-muted transition-[background-color,color,border-color] duration-200 hover:border-border hover:bg-surface-muted hover:text-foreground"
                    onClick={() => void navigate({ to: "/app/insights" })}
                  >
                    {t("today.weekChip", {
                      count: weekPulse.checkIns,
                      days: weekPulse.days,
                    })}
                  </button>
                ) : null}
              </div>
              <Text
                variant="muted"
                className="line-clamp-2 text-sm leading-relaxed"
              >
                {greetingSub}
              </Text>
            </header>

            {comeback ? (
              <Card className="space-y-3 border-primary/15 bg-primary-soft/25 py-3.5">
                <Text variant="label">{t("today.comebackNoStreaks")}</Text>
                <div className="flex gap-2">
                  <Button
                    fullWidth
                    variant="soft"
                    className="min-h-10"
                    onClick={() => {
                      dismissComebackToday();
                      setComeback(null);
                      setPanel("checkin");
                    }}
                  >
                    {t("today.comebackStartCheckIn")}
                  </Button>
                  <Button
                    fullWidth
                    variant="ghost"
                    className="min-h-10"
                    onClick={() => {
                      dismissComebackToday();
                      setComeback(null);
                    }}
                  >
                    {t("today.comebackDismiss")}
                  </Button>
                </div>
              </Card>
            ) : null}

            {/* Primary focus: active / suggested experiment */}
            <Card
              elevation="floating"
              className="space-y-3 border-primary/15 bg-gradient-to-b from-surface-raised to-primary-soft/25 py-5"
            >
              <Text variant="label" className="text-primary/90">
                {assignment &&
                (assignment.recommendationReason === getReasonLabel("pick") ||
                  assignment.recommendationReason ===
                    getReasonLabel("explore") ||
                  assignment.recommendationReason ===
                    t("experiment.reasons.pick"))
                  ? t("today.activeExperiment")
                  : t("today.heroLabel")}
              </Text>

              {loading && hydrated ? (
                <Text variant="muted">{t("today.loadingExperiment")}</Text>
              ) : experiment && assignment && isOpen ? (
                <>
                  <div className="space-y-2">
                    {isWellbeingCategory(experiment.category) ? (
                      <Badge category={experiment.category} />
                    ) : null}
                    <CardTitle as="h2" className="text-xl leading-snug tracking-tight">
                      {experiment.title}
                    </CardTitle>
                    <CardDescription className="line-clamp-2 leading-relaxed">
                      {assignment.recommendationReason ||
                        t("today.experimentFallback")}
                    </CardDescription>
                    <Text variant="caption" className="text-foreground-subtle">
                      {t("today.aboutMinutes", {
                        minutes: experiment.durationMinutes,
                      })}
                    </Text>
                  </div>
                  <div className="space-y-2.5 pt-1">
                    <Button fullWidth onClick={handleStartExperiment}>
                      {assignment.status === "started"
                        ? t("today.continue")
                        : t("today.begin")}
                    </Button>
                    {assignment.status === "assigned" ? (
                      <div className="flex items-center justify-center gap-3 text-xs">
                        <button
                          type="button"
                          disabled={swapping}
                          className="font-medium text-foreground-muted transition-colors duration-200 hover:text-foreground disabled:opacity-50"
                          onClick={() => void handleSwap()}
                        >
                          {swapping ? "…" : t("today.swap")}
                        </button>
                        <span className="text-border" aria-hidden>
                          ·
                        </span>
                        <button
                          type="button"
                          className="font-medium text-foreground-muted transition-colors duration-200 hover:text-foreground"
                          onClick={() => void navigate({ to: "/app/explore" })}
                        >
                          {t("today.pickYourself")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  <Text
                    variant="body"
                    className="leading-relaxed text-foreground-muted"
                  >
                    {assignment?.status === "completed"
                      ? t("today.completedToday")
                      : t("today.findSomething")}
                  </Text>
                  <div className="space-y-2.5 pt-1">
                    <Button
                      fullWidth
                      loading={swapping}
                      onClick={() => void handleSwap()}
                    >
                      {assignment?.status === "completed"
                        ? t("today.tryAnother")
                        : t("today.getSuggestion")}
                    </Button>
                    <button
                      type="button"
                      className="w-full py-1 text-center text-xs font-medium text-foreground-muted transition-colors duration-200 hover:text-foreground"
                      onClick={() => void navigate({ to: "/app/explore" })}
                    >
                      {assignment?.status === "completed"
                        ? t("today.browseExplore")
                        : t("today.pickActivity")}
                    </button>
                  </div>
                </>
              )}
            </Card>

            {/* Soft secondary: check-in + optional first-run / insights */}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  void hapticService.selection();
                  setPanel("checkin");
                }}
                className="flex w-full items-center justify-between gap-3 rounded-2xl border border-border/70 bg-surface-raised px-4 py-3.5 text-left transition-[border-color,background-color,transform] duration-200 ease-out hover:border-border hover:bg-surface-muted/40 active:scale-[0.99]"
              >
                <div className="min-w-0 space-y-0.5">
                  <Text variant="body" className="text-sm font-medium">
                    {t("today.checkInTitle")}
                  </Text>
                  <Text variant="caption" className="text-foreground-subtle">
                    {t("today.checkInLead")}
                  </Text>
                </div>
                <span
                  aria-hidden
                  className="shrink-0 text-foreground-muted"
                >
                  →
                </span>
              </button>

              {showFirstRun ? (
                <button
                  type="button"
                  onClick={() => {
                    void hapticService.selection();
                    setPanel("guide");
                  }}
                  className="px-1 py-1 text-left text-xs font-medium text-primary transition-opacity duration-200 hover:opacity-80"
                >
                  {t("today.firstRunLabel")} →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void navigate({ to: "/app/insights" })}
                  className="px-1 py-1 text-left text-xs font-medium text-foreground-muted transition-colors duration-200 hover:text-foreground"
                >
                  {t("today.openInsights")} →
                </button>
              )}
            </div>

            <ClinicianHomeworkCard />

            <DailyInsightCard
              onOpenLearn={() => void navigate({ to: "/app/learn" })}
              onTryExperiment={(slug) => void handleInsightExperiment(slug)}
              tryBusy={insightBusy}
            />

            {enrollment ? (
              <button
                type="button"
                onClick={() => void navigate({ to: "/app/programs" })}
                className="rounded-2xl border border-border/70 bg-surface-raised/80 px-4 py-3 text-left transition-[border-color,background-color] duration-200 hover:border-border hover:bg-surface-raised"
              >
                <Text variant="label" className="text-foreground-subtle">
                  {t("today.programDay", { day: enrollment.currentDay })}
                </Text>
                <Text
                  variant="body"
                  className="mt-0.5 text-sm font-medium leading-snug"
                >
                  {enrollment.dayTheme}
                </Text>
              </button>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
      <NpsSheet open={npsOpen} onClose={() => setNpsOpen(false)} />
    </Screen>
  );
}
